import type { ResetPasswordState } from '@/pages/ResetPassword/reset-password';

import * as authService from '@/services/auth.service';

import { queryKeys } from '@/query/query-keys';
import {
  notifyMutationErrorWithOptions,
  notifyMutationSuccessWithOptions,
} from '@/query/query-notifications';

import type { ResetPasswordFormValues } from '@/schemas/auth.schema';

import type {
  AuthResponse,
  ForgotPasswordConfirmRequest,
  ResetPasswordChallenge,
} from '@/types/auth.type';
import type { QueryOptions } from '@/types/common.type';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const useLoginMutation = (options?: QueryOptions<AuthResponse | ResetPasswordChallenge>) => {
  return useMutation({
    mutationKey: queryKeys.auth.login,
    mutationFn: authService.loginUser,
    onSuccess: data => {
      notifyMutationSuccessWithOptions(
        options,
        'Logged in',
        'You have been signed in successfully.'
      );
      options?.onSuccess?.(data);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Login failed', error);
      options?.onError?.(error);
    },
  });
};

export const useChangePasswordMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: queryKeys.auth.changePassword,
    mutationFn: authService.changePassword,
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Password changed',
        'Your password was updated successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.users.current });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Password change failed', error);
      options?.onError?.(error);
    },
  });
};

export const useResetPasswordMutation = (options?: QueryOptions<void>) => {
  return useMutation({
    mutationKey: queryKeys.auth.resetPassword,
    mutationFn: ({ data, extra }: { data: ResetPasswordFormValues; extra: ResetPasswordState }) =>
      authService.resetPassword(data, extra),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Password reset',
        'Your password has been reset successfully.'
      );
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Password reset failed', error);
      options?.onError?.(error);
    },
  });
};

export const useForgotPasswordMutation = (options?: QueryOptions<void>) => {
  return useMutation({
    mutationKey: queryKeys.auth.forgotPassword,
    mutationFn: (username: string) => authService.forgotPassword(username),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Request failed', error);
      options?.onError?.(error);
    },
  });
};

export const useForgotPasswordConfirmMutation = (options?: QueryOptions<void>) => {
  return useMutation({
    mutationKey: queryKeys.auth.forgotPasswordConfirm,
    mutationFn: (data: ForgotPasswordConfirmRequest) => authService.forgotPasswordConfirm(data),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Password reset',
        'Your password has been reset successfully.'
      );
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Password reset failed', error);
      options?.onError?.(error);
    },
  });
};
